import { Box } from "@chakra-ui/react";
import { ContentBlock, DraftDecoratorComponentProps } from "draft-js";
import React, { useEffect, useRef } from "react";

import { cloneRegex } from "./constants";

/**
 * Creates a wildcard strategy function that matches a specific wildcard pattern.
 *
 * The strategy function is used by Draft.js decorators to identify and highlight
 * wildcards in the editor content. The pattern should be created using
 * createWildcardPattern() from constants.ts.
 *
 * @param pattern - Regex pattern string to match wildcards (or null for no matching)
 * @returns Strategy function for Draft.js decorator
 *
 * @example
 * ```ts
 * const pattern = createWildcardPattern(['candidate_name', 'job_title']);
 * const strategy = createWildcardStrategy(pattern);
 * const wildcardPlugin = {
 *   decorators: [{
 *     strategy,
 *     component: WildcardSpan,
 *   }],
 * };
 * ```
 */
export const createWildcardStrategy = (pattern: string | null) => {
  // Pre-compile the regex pattern once
  const regex = pattern ? new RegExp(pattern, "g") : null;

  return (
    contentBlock: ContentBlock,
    callback: (start: number, end: number) => void
  ) => {
    if (!regex) return;

    const text = contentBlock.getText();
    const freshRegex = cloneRegex(regex);
    let match = freshRegex.exec(text);

    while (match !== null) {
      callback(match.index, match.index + match[0].length);
      match = freshRegex.exec(text);
    }
  };
};

/**
 * Component to render wildcards with visual highlighting
 * Prevents editing the interior text but allows deletion of the entire wildcard
 */
export const WildcardSpan: React.FC<DraftDecoratorComponentProps> = ({
  children,
}) => {
  const spanRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const span = spanRef.current;
    if (!span) return;

    // Prevent paste inside wildcard
    // Typing prevention is handled by Draft.js-level selectionIsWithinWildcard
    // Deletion is handled by Draft.js-level handleKeyCommand
    const handlePaste = (e: ClipboardEvent) => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      const isWithinWildcard =
        span.contains(range.startContainer) ||
        span.contains(range.endContainer);

      if (isWithinWildcard) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    document.addEventListener("paste", handlePaste, true);

    return () => {
      document.removeEventListener("paste", handlePaste, true);
    };
  }, []);

  return (
    <Box
      ref={spanRef as React.RefObject<HTMLDivElement>}
      as="span"
      bg="blue.50"
      color="blue.700"
      px="0.5"
      py="0.5"
      borderRadius="sm"
      fontFamily="monospace"
      fontSize="sm"
      display="inline"
      data-wildcard="true"
      sx={{
        userSelect: "text", // Allow selection for deletion
        cursor: "default",
      }}
    >
      {children}
    </Box>
  );
};
